import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { formatarDataHora } from '@/lib/formato';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type CartaoCnpj, type EmpresaDetalhe } from '@fiscoai/contracts';

import { useAtualizarEmpresa, useSubstituirLista } from './api';
import { mensagemDeConsulta, useCartaoCnpj } from './api-consultas';
import {
  aplicarDiferencas,
  type ChaveDiferenca,
  type DiferencasCartao,
  diferencasDoCartao,
  inscricoesNovas,
} from './diferencas-cartao';
import { TabelaDiferencas } from './tabela-diferencas';

interface Escolha {
  campos: Set<ChaveDiferenca>;
  cnaes: boolean;
  regimes: boolean;
  inscricoes: boolean;
}

/** Aplica o que foi marcado; devolve quantos itens mudaram. */
function useAplicar(empresa: EmpresaDetalhe) {
  const atualizar = useAtualizarEmpresa(empresa.id);
  const cnaes = useSubstituirLista(empresa.id, 'cnaes');
  const regimes = useSubstituirLista(empresa.id, 'regimes');
  const inscricoes = useSubstituirLista(empresa.id, 'inscricoes-estaduais');
  const aplicar = async (cartao: CartaoCnpj, escolha: Escolha) => {
    const patch = aplicarDiferencas(empresa, cartao, [...escolha.campos]);
    if (Object.keys(patch).length > 0) await atualizar.mutateAsync(patch);
    if (escolha.cnaes) await cnaes.mutateAsync([...cartao.cnaes]);
    if (escolha.regimes) await regimes.mutateAsync([...cartao.regimesSugeridos]);
    const novas = escolha.inscricoes ? inscricoesNovas(empresa, cartao) : [];
    if (novas.length > 0) await inscricoes.mutateAsync([...empresa.inscricoesEstaduais, ...novas]);
    return escolha.campos.size + Number(escolha.cnaes) + Number(escolha.regimes) + novas.length;
  };
  const aplicando =
    atualizar.isPending || cnaes.isPending || regimes.isPending || inscricoes.isPending;
  return { aplicar, aplicando };
}

function semDiferencas(d: DiferencasCartao | null): boolean {
  return d !== null && !d.cnaes && !d.regimes && !d.inscricoes && d.campos.length === 0;
}

function Conteudo(p: {
  consulta: ReturnType<typeof useCartaoCnpj>;
  diferencas: DiferencasCartao | null;
  vazio: boolean;
  escolha: Escolha;
  setEscolha: (mudar: (e: Escolha) => Escolha) => void;
}) {
  const { consulta, diferencas, escolha, setEscolha } = p;
  if (consulta.isPending)
    return (
      <div className="grid gap-2" aria-busy="true">
        <Skeleton className="h-8" />
        <Skeleton className="h-8 w-4/5" />
        <Skeleton className="h-8 w-3/5" />
      </div>
    );
  if (consulta.isError)
    return <p className="text-sm text-perigo">{mensagemDeConsulta(consulta.error, 'CNPJ')}</p>;
  if (!diferencas || !consulta.data) return null;
  if (p.vazio)
    return (
      <p className="text-sm text-texto-suave">
        Cadastro igual ao da Receita (consultado em {formatarDataHora(consulta.data.consultadoEm)}).
      </p>
    );
  return (
    <TabelaDiferencas
      diferencas={diferencas}
      marcadas={escolha.campos}
      alternar={(chave) => {
        setEscolha((e) => {
          const campos = new Set(e.campos);
          if (campos.has(chave)) campos.delete(chave);
          else campos.add(chave);
          return { ...e, campos };
        });
      }}
      cnaes={escolha.cnaes}
      aoMudarCnaes={(cnaes) => {
        setEscolha((e) => ({ ...e, cnaes }));
      }}
      regimes={escolha.regimes}
      aoMudarRegimes={(regimes) => {
        setEscolha((e) => ({ ...e, regimes }));
      }}
      inscricoes={escolha.inscricoes}
      aoMudarInscricoes={(inscricoes) => {
        setEscolha((e) => ({ ...e, inscricoes }));
      }}
    />
  );
}

/** "Atualizar pelo cartão CNPJ": mostra o que mudou na Receita e aplica só o escolhido. */
export function AtualizarPeloCartao({ empresa }: { empresa: EmpresaDetalhe }) {
  const [aberto, setAberto] = useState(false);
  const consulta = useCartaoCnpj();
  const { aplicar, aplicando } = useAplicar(empresa);
  const cartao = consulta.data;
  const diferencas = useMemo(
    () => (cartao ? diferencasDoCartao(empresa, cartao) : null),
    [empresa, cartao],
  );
  const [escolha, setEscolha] = useState<Escolha>({
    campos: new Set(),
    cnaes: true,
    regimes: true,
    inscricoes: true,
  });

  const abrir = () => {
    setAberto(true);
    consulta.mutate(empresa.cnpj, {
      onSuccess: (dados) => {
        const d = diferencasDoCartao(empresa, dados);
        setEscolha({
          campos: new Set(d.campos.map((c) => c.chave)),
          cnaes: true,
          regimes: !d.regimes?.incompleto,
          inscricoes: true,
        });
      },
    });
  };
  const confirmar = () => {
    if (!cartao || !diferencas) return;
    const efetiva = {
      ...escolha,
      cnaes: escolha.cnaes && !!diferencas.cnaes,
      regimes: escolha.regimes && !!diferencas.regimes,
      inscricoes: escolha.inscricoes && !!diferencas.inscricoes,
    };
    aplicar(cartao, efetiva).then(
      (total) => {
        toast.success('Cadastro atualizado pela Receita', {
          description: `${String(total)} item(ns) aplicado(s).`,
        });
        setAberto(false);
      },
      (erro: unknown) => toast.error(mensagemDeErro(erro)),
    );
  };
  const vazio = semDiferencas(diferencas);

  return (
    <>
      <Button variante="secundario" onClick={abrir}>
        <RefreshCw aria-hidden />
        Atualizar pelo cartão CNPJ
      </Button>
      <Dialog
        aberto={aberto}
        aoMudar={setAberto}
        titulo="Atualizar pelo cartão CNPJ"
        descricao="Compare o cadastro com os dados públicos da Receita e escolha o que aplicar."
        acoes={
          <>
            <Button
              variante="fantasma"
              onClick={() => {
                setAberto(false);
              }}
            >
              Fechar
            </Button>
            {diferencas && !vazio && (
              <Button variante="primario" carregando={aplicando} onClick={confirmar}>
                Aplicar selecionados
              </Button>
            )}
          </>
        }
      >
        <Conteudo
          consulta={consulta}
          diferencas={diferencas}
          vazio={vazio}
          escolha={escolha}
          setEscolha={setEscolha}
        />
      </Dialog>
    </>
  );
}
